import React from 'react';
import { GAME_MODE_PRACTICE, GAME_MODE_REGULAR, GameMode, getGameModeLabel } from '../utils/seasonType';

interface GameModeSwitcherProps {
  mode: GameMode;
  onChange: (mode: GameMode) => void;
  className?: string;
  /** light = white pages; dark = navy analytics header */
  variant?: 'light' | 'dark';
}

/**
 * Toggle Regular vs Practice (Intra Squad) within the current active season.
 */
const GameModeSwitcher: React.FC<GameModeSwitcherProps> = ({
  mode,
  onChange,
  className = '',
  variant = 'light',
}) => {
  const isDark = variant === 'dark';
  const current = mode;
  const options: Array<{ value: GameMode; label: string; sub?: string }> = [
    { value: GAME_MODE_REGULAR, label: 'Regular' },
    { value: GAME_MODE_PRACTICE, label: 'Practice', sub: 'Intra Squad' },
  ];

  return (
    <div className={`inline-flex items-center justify-center ${className}`}>
      <div
        className={`inline-flex rounded-full p-0.5 shadow-sm ${
          isDark ? 'bg-black/25 border border-white/20' : 'bg-gray-100 border border-gray-200'
        }`}
      >
        {options.map((option) => {
          const selected = current === option.value;
          const selectedClass =
            option.value === GAME_MODE_PRACTICE ? 'bg-amber-400 text-amber-950 shadow' : 'bg-sd-green text-black shadow';
          const idleClass = isDark ? 'text-white/80 hover:bg-white/10' : 'text-gray-600 hover:bg-white';

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors min-w-[5rem] ${
                selected ? selectedClass : idleClass
              }`}
              title={
                option.value === GAME_MODE_PRACTICE
                  ? 'Practice / Intra Squad games in this season'
                  : 'Regular season games'
              }
              aria-pressed={selected}
            >
              <span className="block leading-tight">{option.label}</span>
              {option.sub && (
                <span
                  className={`block text-[8px] font-normal ${
                    selected ? 'opacity-90' : isDark ? 'text-white/60' : 'text-gray-500'
                  }`}
                >
                  {option.sub}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <span className="sr-only">Current mode: {getGameModeLabel(current)}</span>
    </div>
  );
};

export default GameModeSwitcher;
